import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import { getCurrencySymbol } from "../../constants";
import { useTranslation } from "../../hooks";
import { htmlToPlainLines } from "../../utils/richText";
import { isPackAvailableForMode, isPackAllowedForPatient, } from "./utils/sessionPacks";
const PLAN_CARD_FONT_SIZE = {
    title: { xs: 13, sm: 16 },
    price: { xs: 18, sm: 24 },
    priceUnit: { xs: 11, sm: 14 },
};
export function SessionPacksSection({ packs, selectedPack, selectedDoctor, selectedPatient, patientTab, activeSessionPacks, singleVisit, packQuotes, showAll, consultationMode, dispatch, }) {
    const { t } = useTranslation("appointment");
    const activePacks = packs.filter((p) => {
        if (!p.isActive)
            return false;
        if (!packQuotes[String(p.id)])
            return false;
        if (selectedDoctor &&
            p.doctorIds &&
            p.doctorIds.length > 0 &&
            !p.isAllDoctorsAllowed) {
            if (!p.doctorIds.includes(selectedDoctor.id))
                return false;
        }
        if (!isPackAvailableForMode(p, consultationMode))
            return false;
        if (!isPackAllowedForPatient(p, patientTab, selectedPatient, activeSessionPacks)) {
            return false;
        }
        return true;
    });
    const pricedPacks = activePacks.flatMap((pack) => pack.totalSessions > 0
        ? [{ pack, quote: packQuotes[String(pack.id)] }]
        : []);
    const relevantPacks = pricedPacks;
    if (relevantPacks.length === 0)
        return null;
    const singleVisitPrice = singleVisit
        ? `${getCurrencySymbol(singleVisit.currency)}${singleVisit.amount.toLocaleString()}`
        : null;
    const isSingleVisit = selectedPack === null;
    const initialVisible = 2;
    const visiblePacks = showAll
        ? relevantPacks
        : relevantPacks.slice(0, initialVisible);
    const showToggle = relevantPacks.length > initialVisible;
    const spotlightCandidates = pricedPacks.map((entry) => entry.pack);
    const spotlightPackId = [...spotlightCandidates].sort((left, right) => left.totalSessions - right.totalSessions)[Math.floor((spotlightCandidates.length - 1) / 2)]?.id;
    return (_jsxs(Box, { sx: {
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2.5 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
        }, children: [_jsxs(Stack, { direction: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", spacing: { xs: 0.5, sm: 0 }, sx: { mb: { xs: 1.5, sm: 2 } }, children: [_jsx(Typography, { variant: "h6", children: t("chooseYourPlan") }), showToggle && (_jsxs(Link, { component: "button", variant: "body2", onClick: () => dispatch({ type: "SET_SHOW_ALL_PACKS", payload: !showAll }), sx: {
                            fontSize: 13,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            textDecoration: "none",
                        }, children: [showAll ? t("showLess") : t("viewAll"), _jsx(KeyboardArrowRightIcon, { sx: { fontSize: 16 } })] }))] }), _jsxs(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: { xs: 1, sm: 2 },
                    pb: 0.5,
                }, children: [_jsx(PackCard, { selected: isSingleVisit, featured: false, onClick: () => dispatch({ type: "SET_SELECTED_PACK", payload: null }), children: _jsx(CardContent, { sx: { p: "20px 18px", "&:last-child": { pb: "20px" } }, children: _jsxs(Stack, { spacing: 1.5, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, fontSize: PLAN_CARD_FONT_SIZE.title }, children: t("singleVisit") }), singleVisitPrice ? (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: "row", alignItems: "baseline", spacing: 0.5, sx: { mt: 0.5 }, children: [_jsx(Typography, { variant: "h5", sx: {
                                                                    fontWeight: 700,
                                                                    letterSpacing: -0.5,
                                                                    fontSize: PLAN_CARD_FONT_SIZE.price,
                                                                }, children: singleVisitPrice }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { fontSize: PLAN_CARD_FONT_SIZE.priceUnit }, children: t("perSession") })] }), _jsx(PlanTotalLine, { total: singleVisitPrice, caption: t("billedPerVisit", "billed per visit") })] })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 }, children: t("priceUnavailable", "Price unavailable") }))] }), _jsx(Stack, { spacing: 0.75, children: singleVisitHighlights(t).map((line) => (_jsx(PlanBullet, { text: line }, line))) })] }) }) }), visiblePacks.map(({ pack, quote }) => {
                        const isSelected = selectedPack?.id === pack.id;
                        const symbol = getCurrencySymbol(quote.currency);
                        const highlights = packHighlights(pack, t);
                        return (_jsx(PackCard, { selected: isSelected, featured: pack.id === spotlightPackId, onClick: () => dispatch({ type: "SET_SELECTED_PACK", payload: pack }), children: _jsx(CardContent, { sx: {
                                    p: "20px 18px",
                                    pt: pack.id === spotlightPackId ? "48px" : "20px",
                                    "&:last-child": { pb: "20px" },
                                }, children: _jsxs(Stack, { spacing: 1.5, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", sx: {
                                                        fontWeight: 700,
                                                        fontSize: PLAN_CARD_FONT_SIZE.title,
                                                    }, children: pack.packageName ||
                                                        t("sessionsCount", undefined, {
                                                            count: pack.totalSessions,
                                                        }) }), _jsxs(Stack, { direction: "row", alignItems: "baseline", spacing: 0.5, sx: { mt: 0.5 }, children: [_jsxs(Typography, { variant: "h5", sx: {
                                                                fontWeight: 700,
                                                                letterSpacing: -0.5,
                                                                fontSize: PLAN_CARD_FONT_SIZE.price,
                                                            }, children: [symbol, quote.perSession.toLocaleString()] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { fontSize: PLAN_CARD_FONT_SIZE.priceUnit }, children: t("perSession") })] }), _jsx(PlanTotalLine, { total: `${symbol}${quote.amount.toLocaleString()}`, strikethrough: quote.listAmount !== null &&
                                                        quote.listAmount > quote.amount
                                                        ? `${symbol}${quote.listAmount.toLocaleString()}`
                                                        : undefined, caption: t("totalForSessions", undefined, {
                                                        count: pack.totalSessions,
                                                    }) })] }), _jsx(Stack, { spacing: 0.75, children: highlights.map((line) => (_jsx(PlanBullet, { text: line }, `${pack.id}-${line}`))) })] }) }) }, pack.id));
                    })] })] }));
}
function PackCard({ selected, featured, onClick, children, }) {
    const { t } = useTranslation("appointment");
    let borderColor = "divider";
    let boxShadow = "0 2px 8px rgba(15, 23, 42, 0.04)";
    if (selected) {
        borderColor = "secondary.main";
        boxShadow = "0 0 0 3px rgba(21, 59, 98, 0.08)";
    }
    return (_jsxs(Card, { variant: "outlined", onClick: onClick, sx: {
            borderWidth: selected ? 2 : 1,
            borderColor,
            borderRadius: 2.5,
            cursor: "pointer",
            transition: "all 0.18s ease",
            position: "relative",
            overflow: "hidden",
            bgcolor: "background.paper",
            boxShadow,
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: selected ? boxShadow : "0 8px 20px rgba(15, 23, 42, 0.08)",
            },
        }, children: [selected && (_jsx(CheckCircleIcon, { sx: {
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    fontSize: 20,
                    color: "secondary.main",
                    zIndex: 1,
                } })), children] }));
}
function PlanTotalLine({ total, strikethrough, caption, }) {
    return (_jsxs(Stack, { direction: "row", alignItems: "baseline", flexWrap: "wrap", spacing: 0.5, sx: { mt: 0.25 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 700, fontSize: { xs: 12, sm: 14 } }, children: total }), strikethrough && (_jsx(Typography, { variant: "body2", color: "text.disabled", sx: {
                    textDecoration: "line-through",
                    fontSize: { xs: 11, sm: 13 },
                }, children: strikethrough })), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { fontSize: { xs: 11, sm: 13 } }, children: caption })] }));
}
function PlanBullet({ text }) {
    return (_jsxs(Stack, { direction: "row", spacing: 0.875, alignItems: "flex-start", children: [_jsx(Box, { sx: {
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: "2px",
                }, children: _jsx(CheckIcon, { sx: { fontSize: 11, color: "#fff" } }) }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { lineHeight: 1.45 }, children: text })] }));
}
function singleVisitHighlights(t) {
    return [
        t("oneTimeConsultation"),
        t("prescriptionAndAdvice"),
        t("inClinicOrOnline"),
    ];
}
function packHighlights(pack, t) {
    const fromDescription = htmlToPlainLines(pack.description)
        .flatMap((line) => line.split("."))
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 3);
    if (fromDescription.length > 0) {
        return fromDescription;
    }
    return [
        t("scheduledConsultations", undefined, { count: pack.totalSessions }),
        t("validForDays", undefined, { days: pack.validityDays }),
        pack.allowFamilyBooking
            ? t("familyMembers", undefined, { count: pack.maxFamilyMembers })
            : t("priorityBooking"),
    ];
}
